import { PortableText, type PortableTextProps } from '@portabletext/react';
import { cn } from '@ui-kit/utils';
import type { ComponentProps } from 'react';
import styled from 'styled-components';
import { CmsLink, type CmsLinkProjectionType } from '@/components/CmsLink';
import { Colors } from '@/ui-kit/colors';

export const Legend = ({ className, ...props }: ComponentProps<'legend'>) => (
  <legend className={cn('mb-2', className)} {...props} />
);

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px;
    border: 1px solid ${Colors.BORDER_DEFAULT};
  }
`;

const TableMark = ({ value }: { value?: { rows?: { cells: string[] }[] } }) => {
  if (!value) {
    return null;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <StyledTable>
        <thead>
          <tr>
            {value.rows?.[0]?.cells.map((cell, index) => (
              <th key={index}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {value.rows?.slice(1).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.cells.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </div>
  );
};

export const PortableTextContent = ({ value }: Pick<PortableTextProps, 'value'>) => (
  <PortableText
    value={value}
    components={{
      block: {
        h3: ({ children }) => <h3>{children}</h3>,
        h4: ({ children }) => <h4>{children}</h4>,
        h5: ({ children }) => <h5 className="text-caption-semibold">{children}</h5>,
        // A table annotation renders a block-level <div>, which cannot be nested inside <p>.
        // Sanity stores annotations in markDefs with a _key; spans reference them by that key
        // (not by type name), so we resolve the table mark keys before checking.
        normal: ({ children, value }) => {
          const tableMarkKeys = new Set(
            value.markDefs
              ?.filter((def: { _key: string; _type: string }) => def._type === 'table')
              .map((def: { _key: string }) => def._key) ?? [],
          );
          const containsTable = value.children?.some((child: { _type?: string; marks?: string[] }) =>
            child.marks?.some(mark => tableMarkKeys.has(mark)),
          );
          return containsTable ? <div>{children}</div> : <p>{children}</p>;
        },
      },
      listItem: ({ children }) => <li className="mb-2">{children}</li>,
      marks: {
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        link: ({ children, value }) => <CmsLink link={value as CmsLinkProjectionType}>{children}</CmsLink>,
        table: TableMark,
      },
    }}
  />
);
